/**
 * L'ouvrier retenu sur son téléphone.
 *
 * Chaque matin, la session de la veille a expiré : l'ouvrier devait retaper le
 * code de l'entreprise, retrouver son nom dans la liste, puis son code à
 * quatre chiffres. Son téléphone retient désormais l'entreprise et son nom :
 * il ne tape plus que ses quatre chiffres. Rien de secret n'est gardé — le
 * code à quatre chiffres reste à saisir à chaque fois.
 *
 * Commodité propre à l'appareil : un stockage indisponible (navigation
 * privée) ramène simplement à la saisie complète.
 */

const CLE = 'st_ouvrier_appareil';

/**
 * Retient l'ouvrier sur cet appareil.
 *
 * @param {{codeEntreprise: string, employeId: number, nom: string, entreprise?: string}} ouvrier
 *   ce qu'il faut pour le reconnaître demain
 */
export function retiensOuvrier(ouvrier) {
  if (!ouvrier?.codeEntreprise || ouvrier?.employeId == null) return;
  try {
    localStorage.setItem(
      CLE,
      JSON.stringify({
        codeEntreprise: String(ouvrier.codeEntreprise),
        employeId: ouvrier.employeId,
        nom: ouvrier.nom || '',
        entreprise: ouvrier.entreprise || '',
      }),
    );
  } catch {
    // Stockage indisponible : la saisie complète reste possible.
  }
}

/**
 * @returns {{codeEntreprise: string, employeId: number, nom: string, entreprise: string} | null}
 *   l'ouvrier retenu sur cet appareil, s'il y en a un
 */
export function ouvrierRetenu() {
  try {
    const o = JSON.parse(localStorage.getItem(CLE) || 'null');
    return o && o.codeEntreprise && o.employeId != null ? o : null;
  } catch {
    return null;
  }
}

/** Oublie l'ouvrier retenu : « Ce n'est pas moi ». */
export function oublieOuvrier() {
  try {
    localStorage.removeItem(CLE);
  } catch {
    // Rien à oublier.
  }
}

/**
 * Lit l'ouvrier que l'adresse désigne (« /?ouvrier=CODE.12 »), le retient sur
 * l'appareil et retire le paramètre de l'adresse.
 *
 * <p>C'est l'adresse que rouvre l'icône installée depuis l'invitation : sur
 * iPhone, l'application installée ne voit rien de ce que Safari avait retenu,
 * et l'ouvrier doit pourtant la trouver à son nom dès la première ouverture.
 *
 * @returns {boolean} vrai si l'adresse désignait un ouvrier
 */
export function lisOuvrierDeLAdresse() {
  if (typeof window === 'undefined') return false;
  let parametres;
  try {
    parametres = new URLSearchParams(window.location.search);
  } catch {
    return false;
  }
  const valeur = parametres.get('ouvrier') || '';
  const m = valeur.match(/^([A-Za-z0-9-]{4,24})\.(\d{1,12})$/);
  if (!m) return false;
  const codeEntreprise = m[1].toUpperCase();
  const employeId = Number(m[2]);
  const deja = ouvrierRetenu();
  const meme = deja && deja.codeEntreprise === codeEntreprise && deja.employeId === employeId;
  retiensOuvrier({
    codeEntreprise,
    employeId,
    nom: meme ? deja.nom : '',
    entreprise: meme ? deja.entreprise : '',
  });
  parametres.delete('ouvrier');
  try {
    const reste = parametres.toString();
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname + (reste ? '?' + reste : '') + window.location.hash,
    );
  } catch {
    // Adresse inchangée : sans conséquence.
  }
  return true;
}
